import {
  SerializedError,
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';
import { CardResponse } from '@yleisradio/areena-types';
import { State, ExtraArguments, Dispatch } from '../../client/configureStore';
import * as AreenaAPI from '../../service/areena-api/fetchers';
import { LoadedRange, PaginationData } from '../../client/appTypings';
import { CardWithPointer } from '../view/types';
import { areenaApi } from '../../client/environment';
import { LoggedInUser } from '../../common/types';

export type SearchResultCard = CardWithPointer & {
  imgSrc: string | undefined;
};

export type SearchDataContent = SearchResultCard | null;

export type SearchResults = {
  comscore?: Record<string, string>;
  data?: SearchDataContent[];
  error?: SerializedError;
  loading: boolean;
  loadedRange?: LoadedRange;
  noContent: boolean;
  showSpinner: boolean;
  service: string;
  query?: string;
};

export const initialState: SearchResults = {
  loading: false,
  noContent: false,
  showSpinner: false,
  service: 'tv',
};

export const initialPagination: PaginationData = {
  limit: 40,
  offset: 0,
};

export const searchResultsSlice = createSlice({
  name: 'searchResults',
  initialState,
  reducers: {
    clearSearchResults() {
      return initialState;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadCardPage.pending, (state, { meta }) => {
        state.error = undefined;
        state.loading = true;
        state.noContent = false;

        if (
          !state.data ||
          state.data?.length === 0 ||
          state.query === meta.arg.query
        ) {
          state.data = (state.data || []).concat(
            Array(initialPagination.limit).fill(null)
          );
        } else {
          state.showSpinner = true;
        }
      })
      .addCase(loadCardPage.fulfilled, (state, { payload, meta }) => {
        const data =
          payload.data?.filter((card): card is SearchResultCard =>
            Boolean(card.pointer)
          ) ?? [];

        if (state.query === meta.arg.query) {
          state.data?.splice(
            payload.meta?.offset || 0,
            initialPagination.limit,
            ...data
          );
        } else {
          if (payload.meta?.analytics?.context?.comscore) {
            state.comscore = payload.meta.analytics.context.comscore;
          }
          state.data = data;
        }

        if (!state.data || state.data.length === 0) {
          state.noContent = true;
        }

        state.loadedRange = [
          0,
          initialPagination.limit + (payload.meta?.offset || 0),
        ];
        state.loading = false;
        state.showSpinner = false;
        state.query = meta.arg.query;
      })
      .addCase(loadCardPage.rejected, (state, action) => {
        state.loading = false;
        state.showSpinner = false;
        state.error = action.error;
      });
  },
});

const loadCardPage = createAsyncThunk<
  CardResponse,
  {
    query: string;
    pagination: PaginationData;
    service: string;
    user: LoggedInUser | null;
    searchDestinationUri: string | undefined;
  },
  {
    dispatch: Dispatch;
    state: State;
    extra: ExtraArguments;
  }
>(
  'searchResults/loadPage',
  async (
    { query, pagination, service, user, searchDestinationUri },
    { extra: { authFetch } }
  ) => {
    const searchEndpointFromApi = searchDestinationUri
      ? /(https:\/\/.*)\?/.exec(searchDestinationUri)
      : undefined;

    const searchEndpoint =
      searchEndpointFromApi?.[1] || `${areenaApi.url}/v1/ui/search`;

    return await AreenaAPI.getSearchResults(
      authFetch,
      query,
      service,
      pagination,
      Boolean(user),
      searchEndpoint
    );
  }
);

export const searchResultsSliceActions = {
  loadSearchResultPage: loadCardPage,
  ...searchResultsSlice.actions,
};
